import api from '@/services/api/client';
import {capabilityValues, Capability, getV2Error} from '@/services/api/v2';

export const wyscoutAnalyticsLenses = ['forward', 'midfielder', 'defender', 'goalkeeper'] as const;
export const wyscoutAnalyticsMatchSources = ['workspace', 'external'] as const;
export type WyscoutAnalyticsLens = typeof wyscoutAnalyticsLenses[number];
export type WyscoutAnalyticsMatchSource = typeof wyscoutAnalyticsMatchSources[number];
export type WyscoutAnalyticsAvailability = 'available' | 'unavailable';
export type WyscoutAnalyticsMetricScope = 'aggregate' | 'event';
export type WyscoutAnalyticsShotOutcome = 'goal' | 'saved' | 'blocked' | 'post' | 'wide' | 'on_target' | 'unknown';
export type WyscoutAnalyticsPassOutcome = 'completed' | 'incomplete';
export type WyscoutAnalyticsPassDirection = 'forward' | 'lateral' | 'backward' | 'unknown';
export type WyscoutAnalyticsBodyPart = 'right_foot' | 'left_foot' | 'head_or_other';
export type WyscoutAnalyticsSyncStatus = 'missing' | 'queued' | 'running' | 'complete' | 'partial' | 'failed';

export interface WyscoutAnalyticsFilters {
  lens: WyscoutAnalyticsLens;
  seasonId: number | null;
  matchIds: number[];
}

export interface WyscoutAnalyticsMetric {
  value: number | null;
  total: number | null;
  numerator: number | null;
  denominator: number | null;
  unit: string;
  availability: WyscoutAnalyticsAvailability;
}

export interface WyscoutAnalyticsKPI {
  key: string;
  label: string;
  description: string;
  scope: WyscoutAnalyticsMetricScope;
  metric: WyscoutAnalyticsMetric;
}

export interface WyscoutAnalyticsPoint { x: number; y: number }

export interface WyscoutAnalyticsShotPoint {
  eventId: number;
  matchId: number;
  matchDate: string;
  opponent: string;
  minute: number;
  second: number;
  location: WyscoutAnalyticsPoint;
  outcome: WyscoutAnalyticsShotOutcome;
  xg: number | null;
  postShotXg: number | null;
  bodyPart: string;
  situation: string;
}

export interface WyscoutAnalyticsPassPoint {
  eventId: number;
  matchId: number;
  matchDate: string;
  opponent: string;
  minute: number;
  second: number;
  location: WyscoutAnalyticsPoint;
  outcome: WyscoutAnalyticsPassOutcome;
  direction: WyscoutAnalyticsPassDirection;
}

export interface WyscoutAnalyticsShotMap {
  title: string;
  availability: WyscoutAnalyticsAvailability;
  points: WyscoutAnalyticsShotPoint[];
}

export interface WyscoutAnalyticsPassMap {
  key: string;
  title: string;
  availability: WyscoutAnalyticsAvailability;
  points: WyscoutAnalyticsPassPoint[];
}

export interface WyscoutAnalyticsRankedZone {
  rank: number;
  label: string;
  count: number;
  share: number | null;
}

export interface WyscoutAnalyticsHeatmapBodyPart {
  key: WyscoutAnalyticsBodyPart;
  label: string;
  rankedZones: WyscoutAnalyticsRankedZone[];
}

export interface WyscoutAnalyticsHeatmap {
  title: string;
  availability: WyscoutAnalyticsAvailability;
  columns: number;
  rows: number;
  categories: Array<{key: string; label: string; rankedZones: WyscoutAnalyticsRankedZone[]; bodyParts: WyscoutAnalyticsHeatmapBodyPart[]}>;
  bins: Array<{column: number; row: number; category: string; count: number | null; total: number | null; bodyPartCounts: Partial<Record<WyscoutAnalyticsBodyPart, number>>}>;
}

export interface WyscoutAnalyticsProfile {
  key: string;
  title: string;
  description: string;
  availability: WyscoutAnalyticsAvailability;
  items: Array<{key: string; label: string; metric: WyscoutAnalyticsMetric}>;
}

export interface WyscoutAnalyticsTrend {
  key: string;
  label: string;
  availability: WyscoutAnalyticsAvailability;
  points: Array<{matchId: number; seasonId: number; source: WyscoutAnalyticsMatchSource; matchDate: string; opponent: string; metric: WyscoutAnalyticsMetric}>;
}

export interface WyscoutAnalyticsSyncState {
  status: WyscoutAnalyticsSyncStatus;
  runId: string | null;
  selected: number;
  succeeded: number;
  failed: number;
  attempts: number;
  currentMatchId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
  error: string | null;
}

export interface WyscoutAnalyticsDashboard {
  player: {id: string; wyscoutId: number; name: string; imageUrl: string | null; position: string; defaultLens: WyscoutAnalyticsLens};
  filters: WyscoutAnalyticsFilters;
  filterOptions: {
    seasons: Array<{id: number; label: string}>;
    matches: Array<{id: number; seasonId: number; date: string; opponent: string; competition: string}>;
  };
  context: {appearances: number | null; starts: number | null; minutes: number | null; selectedMatches: number};
  coverage: {selectedMatches: number; advancedStatsMatches: number; eventMatches: number};
  kpis: WyscoutAnalyticsKPI[];
  shotMap: WyscoutAnalyticsShotMap;
  passMap: WyscoutAnalyticsPassMap;
  heatmap: WyscoutAnalyticsHeatmap;
  profiles: WyscoutAnalyticsProfile[];
  trends: WyscoutAnalyticsTrend[];
  sync: WyscoutAnalyticsSyncState;
  analyticsCapabilities: {canSynchronize: boolean; canRetrySync: boolean};
  capabilities: Capability[];
}

export interface WyscoutAnalyticsSynchronization {
  sync: WyscoutAnalyticsSyncState;
  reused: boolean;
}

export const canonicalWyscoutAnalyticsFilters = (value: unknown): WyscoutAnalyticsFilters | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const filters = value as Record<string, unknown>;
  if (!wyscoutAnalyticsLenses.includes(filters.lens as WyscoutAnalyticsLens)) return null;
  if (filters.seasonId !== null
    && (typeof filters.seasonId !== 'number' || !Number.isSafeInteger(filters.seasonId) || filters.seasonId <= 0)) return null;
  if (!Array.isArray(filters.matchIds)) return null;
  const matchIds = filters.matchIds;
  if (matchIds.some(id => typeof id !== 'number' || !Number.isSafeInteger(id) || id <= 0)) return null;
  const canonicalMatchIds = Array.from(new Set(matchIds as number[])).sort((left, right) => left - right);
  if (canonicalMatchIds.length > 100) return null;
  return {
    lens: filters.lens as WyscoutAnalyticsLens,
    seasonId: filters.seasonId as number | null,
    matchIds: canonicalMatchIds,
  };
};

type UnknownRecord = Record<string, unknown>;

const own = (value: UnknownRecord, key: string): boolean => Object.prototype.hasOwnProperty.call(value, key);

const object = (value: unknown, required: readonly string[], optional: readonly string[] = []): UnknownRecord => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new Error('Invalid analytics response');
  const result = value as UnknownRecord;
  const allowed = new Set([...required, ...optional]);
  if (required.some(key => !own(result, key)) || Object.keys(result).some(key => !allowed.has(key))) {
    throw new Error('Invalid analytics response');
  }
  return result;
};

const array = (value: unknown): unknown[] => {
  if (!Array.isArray(value)) throw new Error('Invalid analytics response');
  return value;
};

const string = (value: unknown): string => {
  if (typeof value !== 'string') throw new Error('Invalid analytics response');
  return value;
};

const boolean = (value: unknown): boolean => {
  if (typeof value !== 'boolean') throw new Error('Invalid analytics response');
  return value;
};

const finite = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error('Invalid analytics response');
  return value;
};

const nonNegative = (value: unknown): number => {
  const result = finite(value);
  if (result < 0) throw new Error('Invalid analytics response');
  return result;
};

const integer = (value: unknown, positive = false): number => {
  const result = finite(value);
  if (!Number.isSafeInteger(result) || (positive ? result <= 0 : result < 0)) throw new Error('Invalid analytics response');
  return result;
};

const nullableNumber = (value: unknown, signed = false): number | null =>
  value === null ? null : signed ? finite(value) : nonNegative(value);
const nullableInteger = (value: unknown): number | null => value === null ? null : integer(value);

const optionalNullableNumber = (value: UnknownRecord, key: string, signed = false): number | null =>
  !own(value, key) || value[key] === null ? null : signed ? finite(value[key]) : nonNegative(value[key]);

const optionalNullableString = (value: UnknownRecord, key: string): string | null =>
  !own(value, key) || value[key] === null ? null : string(value[key]);

const date = (value: unknown): string => {
  const result = string(value);
  if (!result || Number.isNaN(Date.parse(result))) throw new Error('Invalid analytics response');
  return result;
};

const optionalDate = (value: UnknownRecord, key: string): string | null => {
  if (!own(value, key) || value[key] === null) return null;
  return date(value[key]);
};

const oneOf = <T extends string>(value: unknown, values: readonly T[]): T => {
  const result = string(value);
  if (!values.includes(result as T)) throw new Error('Invalid analytics response');
  return result as T;
};

const wyscoutAnalyticsBodyParts = ['right_foot', 'left_foot', 'head_or_other'] as const;

const lens = (value: unknown): WyscoutAnalyticsLens => oneOf(value, wyscoutAnalyticsLenses);
const availability = (value: unknown): WyscoutAnalyticsAvailability => oneOf(value, ['available', 'unavailable'] as const);

const safeImage = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const result = value;
  if (!result) return null;
  const unsafeCharacter = Array.from(result).some(character => {
    const code = character.charCodeAt(0);
    return code <= 0x20 || (code >= 0x7f && code <= 0x9f) || character === '\\';
  });
  if (unsafeCharacter || /%(?:0[0-9a-f]|1[0-9a-f]|2f|5c|7f)/i.test(result)) {
    return null;
  }
  if (/^\/assets\/players\/[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}\.(?:jpg|png|webp|gif)$/.test(result)) {
    return result;
  }
  if (!result.startsWith('https://')) return null;
  try {
    const parsed = new URL(result);
    if (parsed.protocol === 'https:' && parsed.hostname && !parsed.username && !parsed.password) return parsed.toString();
  } catch {
    // Rejected below.
  }
  return null;
};

const signedGoalsPrevented = (key: string): boolean => key === 'goals_prevented' || key.startsWith('goals_prevented_');

const metric = (value: unknown, signed = false): WyscoutAnalyticsMetric => {
  const dto = object(value, ['value', 'unit', 'availability'], ['total', 'numerator', 'denominator']);
  const result: WyscoutAnalyticsMetric = {
    value: nullableNumber(dto.value, signed), total: optionalNullableNumber(dto, 'total', signed),
    numerator: optionalNullableNumber(dto, 'numerator'), denominator: optionalNullableNumber(dto, 'denominator'),
    unit: string(dto.unit), availability: availability(dto.availability),
  };
  if ((result.availability === 'available') !== (result.value !== null)) throw new Error('Invalid analytics response');
  if (result.availability === 'available' && result.unit === 'per_90'
    && (result.total === null || result.denominator === null || result.denominator <= 0 || result.numerator !== null)) {
    throw new Error('Invalid analytics response');
  }
  if (result.availability === 'available' && result.unit === 'percent'
    && (result.numerator === null || result.denominator === null || result.denominator <= 0 || result.total !== null)) {
    throw new Error('Invalid analytics response');
  }
  return result;
};

const point = (value: unknown): WyscoutAnalyticsPoint => {
  const dto = object(value, ['x', 'y']);
  const x = finite(dto.x);
  const y = finite(dto.y);
  if (x < 0 || x > 100 || y < 0 || y > 100) throw new Error('Invalid analytics response');
  return {x, y};
};

const shotPoint = (value: unknown): WyscoutAnalyticsShotPoint => {
  const dto = object(value, [
    'event_id', 'match_id', 'match_date', 'opponent', 'minute', 'second', 'location', 'outcome',
    'xg', 'post_shot_xg', 'body_part', 'situation',
  ]);
  return {
    eventId: integer(dto.event_id, true), matchId: integer(dto.match_id, true), matchDate: date(dto.match_date),
    opponent: string(dto.opponent), minute: integer(dto.minute), second: integer(dto.second), location: point(dto.location),
    outcome: oneOf(dto.outcome, ['goal', 'saved', 'blocked', 'post', 'wide', 'on_target', 'unknown'] as const),
    xg: nullableNumber(dto.xg), postShotXg: nullableNumber(dto.post_shot_xg), bodyPart: string(dto.body_part),
    situation: string(dto.situation),
  };
};

const passPoint = (value: unknown): WyscoutAnalyticsPassPoint => {
  const dto = object(value, [
    'event_id', 'match_id', 'match_date', 'opponent', 'minute', 'second', 'location', 'outcome', 'direction',
  ]);
  return {
    eventId: integer(dto.event_id, true), matchId: integer(dto.match_id, true), matchDate: date(dto.match_date),
    opponent: string(dto.opponent), minute: integer(dto.minute), second: integer(dto.second), location: point(dto.location),
    outcome: oneOf(dto.outcome, ['completed', 'incomplete'] as const),
    direction: oneOf(dto.direction, ['forward', 'lateral', 'backward', 'unknown'] as const),
  };
};

const rankedZone = (value: unknown): WyscoutAnalyticsRankedZone => {
  const dto = object(value, ['rank', 'label', 'count', 'share']);
  return {
    rank: integer(dto.rank, true), label: string(dto.label), count: integer(dto.count), share: nullableNumber(dto.share),
  };
};

const bodyPartCounts = (value: unknown): Partial<Record<WyscoutAnalyticsBodyPart, number>> => {
  const dto = object(value, [], wyscoutAnalyticsBodyParts);
  return Object.fromEntries(Object.entries(dto).map(([key, count]) => [
    oneOf(key, wyscoutAnalyticsBodyParts), integer(count),
  ]));
};

const syncState = (value: unknown): WyscoutAnalyticsSyncState => {
  const dto = object(value, ['status', 'selected', 'succeeded', 'failed', 'attempts'], [
    'run_id', 'current_match_id', 'created_at', 'updated_at', 'completed_at', 'error',
  ]);
  return {
    status: oneOf(dto.status, ['missing', 'queued', 'running', 'complete', 'partial', 'failed'] as const),
    runId: optionalNullableString(dto, 'run_id'), selected: integer(dto.selected), succeeded: integer(dto.succeeded),
    failed: integer(dto.failed), attempts: integer(dto.attempts),
    currentMatchId: own(dto, 'current_match_id') && dto.current_match_id !== null ? integer(dto.current_match_id, true) : null,
    createdAt: optionalDate(dto, 'created_at'), updatedAt: optionalDate(dto, 'updated_at'),
    completedAt: optionalDate(dto, 'completed_at'), error: optionalNullableString(dto, 'error'),
  };
};

const mapDashboard = (value: unknown): WyscoutAnalyticsDashboard => {
  const envelope = object(value, ['data', 'capabilities']);
  const dto = object(envelope.data, [
    'player', 'lens', 'filters', 'filter_options', 'context', 'coverage', 'kpis', 'shot_map', 'pass_map',
    'heatmap', 'profiles', 'trends', 'sync', 'capabilities',
  ]);
  const activeLens = lens(dto.lens);
  const player = object(dto.player, ['id', 'wyscout_id', 'name', 'position', 'default_lens'], ['image_url']);
  const filters = object(dto.filters, ['season_id', 'match_ids']);
  const filterOptions = object(dto.filter_options, ['seasons', 'matches']);
  const context = object(dto.context, ['appearances', 'starts', 'minutes', 'selected_matches']);
  const coverage = object(dto.coverage, ['selected_matches', 'advanced_stats_matches', 'event_matches']);
  const shotMap = object(dto.shot_map, ['title', 'availability', 'points']);
  const passMap = object(dto.pass_map, ['key', 'title', 'availability', 'points']);
  const heatmap = object(dto.heatmap, ['title', 'availability', 'columns', 'rows', 'categories', 'bins']);
  const analyticsCapabilities = object(dto.capabilities, ['can_synchronize', 'can_retry_sync']);
  const imageUrl = own(player, 'image_url') ? safeImage(player.image_url) : null;

  return {
    player: {
      id: string(player.id), wyscoutId: integer(player.wyscout_id, true), name: string(player.name),
      imageUrl, position: string(player.position), defaultLens: lens(player.default_lens),
    },
    filters: {
      lens: activeLens,
      seasonId: filters.season_id === null ? null : integer(filters.season_id, true),
      matchIds: array(filters.match_ids).map(value => integer(value, true)),
    },
    filterOptions: {
      seasons: array(filterOptions.seasons).map(value => {
        const option = object(value, ['id', 'label']);
        return {id: integer(option.id, true), label: string(option.label)};
      }),
      matches: array(filterOptions.matches).map(value => {
        const option = object(value, ['id', 'season_id', 'date', 'opponent', 'competition']);
        return {
          id: integer(option.id, true), seasonId: integer(option.season_id, true), date: date(option.date),
          opponent: string(option.opponent), competition: string(option.competition),
        };
      }),
    },
    context: {
      appearances: nullableInteger(context.appearances), starts: nullableInteger(context.starts),
      minutes: nullableNumber(context.minutes), selectedMatches: integer(context.selected_matches),
    },
    coverage: {
      selectedMatches: integer(coverage.selected_matches), advancedStatsMatches: integer(coverage.advanced_stats_matches),
      eventMatches: integer(coverage.event_matches),
    },
    kpis: array(dto.kpis).map(value => {
      const kpi = object(value, ['key', 'label', 'scope', 'metric'], ['description']);
      const key = string(kpi.key);
      return {
        key, label: string(kpi.label), description: own(kpi, 'description') ? string(kpi.description) : '',
        scope: oneOf(kpi.scope, ['aggregate', 'event'] as const), metric: metric(kpi.metric, signedGoalsPrevented(key)),
      };
    }),
    shotMap: {title: string(shotMap.title), availability: availability(shotMap.availability), points: array(shotMap.points).map(shotPoint)},
    passMap: {
      key: string(passMap.key), title: string(passMap.title), availability: availability(passMap.availability),
      points: array(passMap.points).map(passPoint),
    },
    heatmap: {
      title: string(heatmap.title), availability: availability(heatmap.availability),
      columns: integer(heatmap.columns, true), rows: integer(heatmap.rows, true),
      categories: array(heatmap.categories).map(value => {
        const category = object(value, ['key', 'label', 'ranked_zones', 'body_parts']);
        return {
          key: string(category.key), label: string(category.label),
          rankedZones: array(category.ranked_zones).map(rankedZone),
          bodyParts: array(category.body_parts).map(value => {
            const bodyPart = object(value, ['key', 'label', 'ranked_zones']);
            return {
              key: oneOf(bodyPart.key, wyscoutAnalyticsBodyParts), label: string(bodyPart.label),
              rankedZones: array(bodyPart.ranked_zones).map(rankedZone),
            };
          }),
        };
      }),
      bins: array(heatmap.bins).map(value => {
        const bin = object(value, ['column', 'row', 'category', 'count', 'total', 'body_part_counts']);
        return {
          column: integer(bin.column), row: integer(bin.row), category: string(bin.category),
          count: bin.count === null ? null : integer(bin.count), total: bin.total === null ? null : integer(bin.total),
          bodyPartCounts: bodyPartCounts(bin.body_part_counts),
        };
      }),
    },
    profiles: array(dto.profiles).map(value => {
      const profile = object(value, ['key', 'title', 'availability', 'items'], ['description']);
      return {
        key: string(profile.key), title: string(profile.title),
        description: own(profile, 'description') ? string(profile.description) : '',
        availability: availability(profile.availability),
        items: array(profile.items).map(itemValue => {
          const item = object(itemValue, ['key', 'label', 'metric']);
          return {key: string(item.key), label: string(item.label), metric: metric(item.metric)};
        }),
      };
    }),
    trends: array(dto.trends).map(value => {
      const trend = object(value, ['key', 'label', 'availability', 'points']);
      const key = string(trend.key);
      return {
        key, label: string(trend.label), availability: availability(trend.availability),
        points: array(trend.points).map(pointValue => {
          const trendPoint = object(pointValue, ['match_id', 'season_id', 'source', 'match_date', 'opponent', 'metric']);
          return {
            matchId: integer(trendPoint.match_id, true), seasonId: integer(trendPoint.season_id, true),
            source: oneOf(trendPoint.source, wyscoutAnalyticsMatchSources), matchDate: date(trendPoint.match_date),
            opponent: string(trendPoint.opponent), metric: metric(trendPoint.metric, signedGoalsPrevented(key)),
          };
        }),
      };
    }),
    sync: syncState(dto.sync),
    analyticsCapabilities: {
      canSynchronize: boolean(analyticsCapabilities.can_synchronize),
      canRetrySync: boolean(analyticsCapabilities.can_retry_sync),
    },
    capabilities: array(envelope.capabilities).map(value => oneOf(value, capabilityValues)),
  };
};

const mapSynchronization = (value: unknown): WyscoutAnalyticsSynchronization => {
  const envelope = object(value, ['data', 'reused']);
  const run = object(envelope.data, [
    'id', 'workspace_id', 'player_id', 'status', 'selected_count', 'succeeded_count', 'failed_count',
    'attempt_count', 'created_at', 'updated_at',
  ], ['current_match_id', 'failure_summary', 'completed_at', 'source_import_id']);
  const status = oneOf(run.status, ['queued', 'running', 'completed', 'partial', 'failed'] as const);
  optionalNullableString(run, 'failure_summary');
  optionalNullableString(run, 'source_import_id');
  string(run.workspace_id);
  string(run.player_id);
  return {
    sync: {
      status: status === 'completed' ? 'complete' : status,
      runId: string(run.id), selected: integer(run.selected_count), succeeded: integer(run.succeeded_count),
      failed: integer(run.failed_count), attempts: integer(run.attempt_count),
      currentMatchId: own(run, 'current_match_id') && run.current_match_id !== null ? integer(run.current_match_id, true) : null,
      createdAt: date(run.created_at), updatedAt: date(run.updated_at), completedAt: optionalDate(run, 'completed_at'),
      error: null,
    },
    reused: boolean(envelope.reused),
  };
};

const requestParams = (filters: WyscoutAnalyticsFilters): URLSearchParams => {
  const canonical = canonicalWyscoutAnalyticsFilters(filters);
  if (canonical === null) throw new Error('Invalid analytics filters');
  const params = new URLSearchParams({lens: canonical.lens});
  if (canonical.seasonId !== null) params.set('season_id', String(canonical.seasonId));
  canonical.matchIds.forEach(matchId => params.append('match_id', String(matchId)));
  return params;
};

const rethrow = (error: unknown): never => {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') throw error;
  if (typeof error === 'object' && error !== null) {
    const cancelled = error as UnknownRecord;
    if (cancelled.name === 'CanceledError' || cancelled.code === 'ERR_CANCELED') throw error;
  }
  throw getV2Error(error);
};

export const wyscoutAnalyticsAPI = {
  get: async (
    workspaceId: string,
    playerId: string,
    filters: WyscoutAnalyticsFilters,
    signal: AbortSignal,
  ): Promise<WyscoutAnalyticsDashboard> => {
    try {
      const response = await api.get<unknown>(`/v2/players/${playerId}/wyscout-analytics`, {
        headers: {'X-Workspace-ID': workspaceId}, params: requestParams(filters), signal,
      });
      return mapDashboard(response.data);
    } catch (error) {
      return rethrow(error);
    }
  },
  synchronize: async (
    workspaceId: string,
    playerId: string,
    signal: AbortSignal,
  ): Promise<WyscoutAnalyticsSynchronization> => {
    try {
      const response = await api.post<unknown>(
        `/v2/players/${playerId}/wyscout-analytics/synchronizations`, undefined,
        {headers: {'X-Workspace-ID': workspaceId}, signal},
      );
      return mapSynchronization(response.data);
    } catch (error) {
      return rethrow(error);
    }
  },
};
